import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { AdminBoxCollectionItemResponse } from '../responses/admin-box-collection-item.response';
import { blank, Guid } from '@web/utils';
import { AdminBoxCreateRequest } from '../requests/admin-box-create.request';
import { AdminBoxDetailsResponse } from '../responses/admin-box-details.response';
import { AdminBoxQuantityHistoryCollectionItemResponse } from '../responses/admin-box-quantity-history-collection-item.response';
import { AdminBoxUpdateRequest } from '../requests/admin-box-update.request';
import { AdminBoxCompleteOrdersResponse } from '../responses/admin-box-complete-orders.response';

@Injectable({
  providedIn: 'root',
})
export class AdminBoxApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(
    request: CollectionRequest,
  ): Observable<CollectionResponse<AdminBoxCollectionItemResponse>> {
    const path = 'admin/box';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBoxCollectionItemResponse,
          ),
        ),
      );
  }

  /**
   * @deprecated
   */
  /* prettier-ignore */
  public removeById(id: Guid): Observable<void> {
    const path = `admin/box/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http.delete(url).pipe(blank())
  }

  public create(request: AdminBoxCreateRequest): Observable<void> {
    const path = `admin/box`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBoxCreateRequest,
    );

    return this.provider.http.post(url, body, { context }).pipe(blank());
  }

  public createSale(id: Guid): Observable<void> {
    const path = `admin/box/${HttpParams.Id}/sale`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.post(url, {}, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public fetchDetailsById(id: Guid): Observable<AdminBoxDetailsResponse> {
    const path = `admin/box/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(AdminBoxDetailsResponse),
        ),
      );
  }

  /* prettier-ignore */
  public update(
    id: Guid,
    request: AdminBoxUpdateRequest,
  ): Observable<void> {
    const path = `admin/box/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();
    const body = this.provider.httpHelper.requestToBody(
      request,
      AdminBoxUpdateRequest,
    );

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  public fetchQuantityHistoryCollection(params: {
    request: CollectionRequest;
    id: Guid;
  }): Observable<
    CollectionResponse<AdminBoxQuantityHistoryCollectionItemResponse>
  > {
    const path = `admin/box/${HttpParams.Id}/quantity/history`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(
            params.request,
          ),
        )
        .params({
          [HttpParams.Id]: params.id,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminBoxQuantityHistoryCollectionItemResponse,
          ),
        ),
      );
  }

  public completeOrders(
    id: Guid
  ): Observable<AdminBoxCompleteOrdersResponse> {
    const path = `admin/box/${HttpParams.Id}/orders/complete`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http
      .post(url, {})
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBoxCompleteOrdersResponse,
          ),
        ),
      );
  }

  public simulateCompleteOrders(
    id: Guid
  ): Observable<AdminBoxCompleteOrdersResponse> {
    const path = `admin/box/${HttpParams.Id}/simulate/orders/complete`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http
      .post(url, {})
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBoxCompleteOrdersResponse,
          ),
        ),
      );
  }

  public downloadLabels(
    id: Guid
  ): Observable<AdminBoxCompleteOrdersResponse> {
    const path = `admin/box/${HttpParams.Id}/labels`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http
      .get(url, {})
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminBoxCompleteOrdersResponse,
          ),
        ),
      );
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
